import './CharacterCreationFlowStepper.css';
import { DispatchCardDismiss } from './DispatchCardDismiss';

/**
 * V9.2.11 (2026-06-06) — CharacterCreationFlowStepper.
 *
 * Pure-presentation render of a `force_character_creation_validate`
 * dispatch action. Ch 2 character creation: rank + gender + physical
 * (height/weight/appearance) + 5 characteristics (STR/DEX/WIL/PER/INT).
 *
 * Schema 1-to-1 with the V9.1.c tool args at routes/dispatch.py:939-1005:
 *   - rank: enum {Rookie, Agent, "00"} (required)
 *   - gender: enum {male, female} (required) — affects Fame (female -40
 *     per md L1124)
 *   - height_inches: int 48-84 (optional)
 *   - weight_lbs: int 80-280 (optional, must be within 2 bands of
 *     height per md L1063)
 *   - appearance: enum {plain, normal, good_looking, attractive,
 *     striking, sensational} (required)
 *   - characteristics: {STR, DEX, WIL, PER, INT} each int 1-15 (required)
 *   - rationale: GM voice-line ≤30 words (optional)
 *
 * Generation Point cost per characteristic: 5 (free base) → 15 (max).
 * Pure-presentation surface; engine validation occurs downstream — pass
 * `validationErrors` to surface engine errors inline.
 */

export type PcRank = 'Rookie' | 'Agent' | '00';
export type PcGender = 'male' | 'female';
export type PcAppearance =
  | 'plain'
  | 'normal'
  | 'good_looking'
  | 'attractive'
  | 'striking'
  | 'sensational';

export interface PcCharacteristics {
  STR: number;
  DEX: number;
  WIL: number;
  PER: number;
  INT: number;
}

export interface CharacterCreationFlowStepperProps {
  rank: PcRank;
  gender: PcGender;
  heightInches?: number;
  weightLbs?: number;
  appearance: PcAppearance;
  characteristics: PcCharacteristics;
  rationale?: string;
  /** Engine-computed validation errors. Empty/omitted = valid. */
  validationErrors?: string[];
  /** Engine-computed final Fame Points (post-rank + appearance + gender). */
  resolvedFamePoints?: number | null;
  /** V9.2.x.3: when provided, renders a ✕ dismiss button (top-right). */
  onDismiss?: () => void;
  testidPrefix?: string;
}

const APPEARANCE_LABEL: Record<PcAppearance, string> = {
  plain: 'Plain',
  normal: 'Normal',
  good_looking: 'Good-looking',
  attractive: 'Attractive',
  striking: 'Striking',
  sensational: 'Sensational',
};

const GENDER_LABEL: Record<PcGender, string> = {
  male: 'Male',
  female: 'Female',
};

const CHARACTERISTIC_KEYS: ReadonlyArray<keyof PcCharacteristics> = [
  'STR',
  'DEX',
  'WIL',
  'PER',
  'INT',
] as const;

function formatHeight(inches: number): string {
  const ft = Math.floor(inches / 12);
  const inRem = inches % 12;
  return `${ft}′${inRem}″ (${inches}″)`;
}

export function CharacterCreationFlowStepper({
  rank,
  gender,
  heightInches,
  weightLbs,
  appearance,
  characteristics,
  rationale,
  validationErrors,
  resolvedFamePoints,
  onDismiss,
  testidPrefix = 'br-chargen',
}: CharacterCreationFlowStepperProps) {
  const hasErrors = validationErrors !== undefined && validationErrors.length > 0;
  const hasFame = typeof resolvedFamePoints === 'number';

  return (
    <aside
      className="br-chargen"
      data-testid={testidPrefix}
      data-rank={rank}
      data-gender={gender}
      data-appearance={appearance}
      data-state={hasErrors ? 'invalid' : 'valid'}
    >
      <DispatchCardDismiss onDismiss={onDismiss} testidPrefix={testidPrefix} />
      <header className="br-chargen-header">
        <span className="br-chargen-label">Chargen</span>
        <span
          className="br-chargen-rank-chip"
          data-testid={`${testidPrefix}-rank-chip`}
          data-rank-id={rank}
        >
          {rank}
        </span>
        <span
          className="br-chargen-gender"
          data-testid={`${testidPrefix}-gender`}
          data-gender-id={gender}
        >
          {GENDER_LABEL[gender]}
        </span>
      </header>

      <div
        className="br-chargen-physical"
        data-testid={`${testidPrefix}-physical`}
      >
        {typeof heightInches === 'number' && (
          <span
            className="br-chargen-physical-cell br-label"
            data-testid={`${testidPrefix}-height`}
          >
            <span className="br-chargen-physical-label">Height</span>
            <span className="br-chargen-physical-value">
              {formatHeight(heightInches)}
            </span>
          </span>
        )}
        {typeof weightLbs === 'number' && (
          <span
            className="br-chargen-physical-cell br-label"
            data-testid={`${testidPrefix}-weight`}
          >
            <span className="br-chargen-physical-label">Weight</span>
            <span className="br-chargen-physical-value">{weightLbs} lbs</span>
          </span>
        )}
        <span
          className="br-chargen-physical-cell"
          data-testid={`${testidPrefix}-appearance`}
          data-appearance-id={appearance}
        >
          <span className="br-chargen-physical-label">Appearance</span>
          <span className="br-chargen-physical-value">
            {APPEARANCE_LABEL[appearance]}
          </span>
        </span>
      </div>

      <div
        className="br-chargen-characteristics"
        data-testid={`${testidPrefix}-characteristics`}
      >
        {CHARACTERISTIC_KEYS.map((key) => (
          <div
            key={key}
            className="br-chargen-characteristic-cell"
            data-testid={`${testidPrefix}-char-${key}`}
            data-characteristic={key}
          >
            <span className="br-chargen-characteristic-key">{key}</span>
            <span className="br-chargen-characteristic-value br-label">
              {characteristics[key]}
            </span>
          </div>
        ))}
      </div>

      {hasErrors ? (
        <ul
          className="br-chargen-errors"
          data-testid={`${testidPrefix}-errors`}
        >
          {validationErrors!.map((err, idx) => (
            <li
              key={idx}
              className="br-chargen-error"
              data-testid={`${testidPrefix}-error-${idx}`}
            >
              {err}
            </li>
          ))}
        </ul>
      ) : (
        <p
          className="br-chargen-valid-note"
          data-testid={`${testidPrefix}-valid-note`}
        >
          Spending record valid.
        </p>
      )}

      {hasFame && (
        <div
          className="br-chargen-fame"
          data-testid={`${testidPrefix}-fame`}
        >
          <span className="br-chargen-fame-label">Final Fame Points</span>
          <span
            className="br-chargen-fame-value br-label"
            data-testid={`${testidPrefix}-fame-points`}
          >
            {resolvedFamePoints}
          </span>
        </div>
      )}

      {rationale !== undefined && rationale.length > 0 && (
        <p
          className="br-chargen-rationale"
          data-testid={`${testidPrefix}-rationale`}
        >
          {rationale}
        </p>
      )}
    </aside>
  );
}
